import { useCallback, useEffect, useState } from 'react';
import type { User } from '@loggy/shared';

import { Spinner } from './design/index.js';
import { fetchMe, logout } from './api.js';
import { Dashboard } from './screens/Dashboard.js';
import { SignIn } from './screens/SignIn.js';
import { InviteAccept } from './screens/InviteAccept.js';
import styles from './App.module.css';

type State =
  | { status: 'loading' }
  | { status: 'signed-out'; error: string | null }
  | { status: 'signed-in'; user: User };

const INVITE_PATH = '/invites/accept';
const PENDING_TOKEN_KEY = 'loggy:pendingInviteToken';

/**
 * Reads the invitation token this visit is carrying, persisting it the
 * moment it is found — before this module has finished being evaluated,
 * let alone before `fetchMe` runs or a sign-in link is clicked.
 *
 * `POST /api/invites` hands out a link to `/invites/accept?token=...`, but
 * that query string does not survive a visitor with no account: they get
 * sent to Google, and the server's OAuth callback always redirects back to
 * a fixed `/` (see `api/src/routes/auth.ts`), no query string attached.
 * sessionStorage is what carries the token across that gap instead — it is
 * scoped to this tab and this origin, and a same-tab trip to Google's
 * origin and back leaves it untouched, which is exactly the round trip a
 * fresh visitor makes.
 *
 * A visitor already signed in on this first landing never leaves this
 * origin at all, so returning the token straight from the URL — even if
 * sessionStorage is unavailable — still lets them accept.
 */
function readPendingInviteToken(): string | null {
  if (window.location.pathname === INVITE_PATH) {
    const token = new URLSearchParams(window.location.search).get('token');
    if (token) {
      try {
        sessionStorage.setItem(PENDING_TOKEN_KEY, token);
      } catch {
        // Private browsing, blocked site data, or similar — nothing to do
        // but carry on with the token this render already has in hand.
      }
      return token;
    }
  }
  // No token on this URL: either a plain visit, or the visitor is back from
  // the Google round trip and it is sessionStorage's turn to have it.
  try {
    return sessionStorage.getItem(PENDING_TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Forgets the pending invitation and drops the token from the URL, so a
 *  refresh — or a later visit — never replays it. */
function clearPendingInviteToken(): void {
  try {
    sessionStorage.removeItem(PENDING_TOKEN_KEY);
  } catch {
    // Nothing was persisted in the first place; nothing to clear.
  }
  window.history.replaceState(null, '', '/');
}

// Read once, at module load — deliberately outside the component, so
// nothing (a re-render, StrictMode remounting the tree) can invoke it a
// second time and race the URL against a store that the first read may
// have already written to.
const initialPendingToken = readPendingInviteToken();

export function App() {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [pendingToken, setPendingToken] = useState<string | null>(initialPendingToken);

  const load = useCallback(async () => {
    try {
      const user = await fetchMe();
      setState(user ? { status: 'signed-in', user } : { status: 'signed-out', error: null });
    } catch (err) {
      // A failure here means the server is unreachable or broken — not that the
      // person is signed out. Saying "sign in" would send them round a loop that
      // cannot succeed, so say what actually happened.
      setState({
        status: 'signed-out',
        error: err instanceof Error ? err.message : 'Something went wrong reaching the server.',
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSignOut = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      await logout();
    } finally {
      // Whether or not the server acknowledged it, re-read the truth rather
      // than assuming we are signed out.
      await load();
    }
  }, [load]);

  /**
   * The token is spent — the server has answered, whichever way.
   *
   * Deliberately does NOT leave the screen: the reader still has to see what
   * happened. Retiring it here rather than on the way out is what stops a
   * refresh on the success screen from re-sending an invitation that has
   * already been accepted, which the server refuses and which then reads as
   * "this link is no longer valid" for a share that actually worked.
   */
  const handleInviteRetired = useCallback(() => {
    clearPendingInviteToken();
  }, []);

  /** The reader is done with the invitation screen; show them the app. */
  const handleInviteSettled = useCallback(() => {
    clearPendingInviteToken();
    setPendingToken(null);
  }, []);

  if (state.status === 'loading') {
    return (
      <div className={styles.loading} role="status" aria-live="polite">
        <Spinner />
        <span className={styles.srOnly}>Loading</span>
      </div>
    );
  }

  if (state.status === 'signed-in') {
    if (pendingToken) {
      // The grant binds to whoever is signed in when it is accepted, so this
      // is the only place `acceptInviteByToken` is ever reachable from.
      return (
        <InviteAccept
          token={pendingToken}
          onAccepted={handleInviteSettled}
          onDismiss={handleInviteSettled}
          onSettled={handleInviteRetired}
        />
      );
    }
    return <Dashboard user={state.user} onSignOut={() => void handleSignOut()} />;
  }

  // Signed out. Never accept from here — there is nobody yet to bind the
  // grant to — just explain why signing in matters right now.
  return (
    <SignIn
      error={state.error}
      reason={pendingToken ? 'Sign in to accept the dashboard someone shared with you' : null}
    />
  );
}
